export type PhotoUploadErrorCode =
  | 'PHOTO_INVALID_TYPE'
  | 'PHOTO_TOO_LARGE'
  | 'PHOTO_PROCESSING_FAILED'
  | 'PHOTO_UPLOAD_NETWORK_ERROR'
  | 'PHOTO_UPLOAD_STORAGE_ERROR'
  | 'PHOTO_UPLOAD_DATABASE_ERROR';

export class PhotoUploadError extends Error {
  code: PhotoUploadErrorCode;

  constructor(code: PhotoUploadErrorCode, message: string) {
    super(message);
    this.name = 'PhotoUploadError';
    this.code = code;
  }
}

const NETWORK_MARKERS = [
  'failed to fetch',
  'network',
  'fetcherror',
  'networkrequestfailed',
  'load failed',
];

const looksLikeNetworkError = (message: string): boolean => {
  const normalized = message.toLowerCase();
  return NETWORK_MARKERS.some((marker) => normalized.includes(marker));
};

const getErrorCode = (error: unknown): string | null => {
  if (error && typeof error === 'object' && 'code' in error) {
    const code = (error as { code?: unknown }).code;
    if (typeof code === 'string') {
      return code;
    }
  }

  return null;
};

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }

  return String(error || '');
};

export const resolvePhotoUploadErrorMessage = (
  t: (key: string) => string,
  error: unknown,
  fallbackKey: string = 'photos.uploadError'
): string => {
  const code = getErrorCode(error);
  const message = getErrorMessage(error);

  if (code === 'PHOTO_INVALID_TYPE') {
    return t('photos.invalidType');
  }

  if (code === 'PHOTO_TOO_LARGE') {
    return t('photos.fileTooLarge');
  }

  if (code === 'PHOTO_PROCESSING_FAILED') {
    return t('photos.compressionFailed');
  }

  if (code === 'PHOTO_UPLOAD_NETWORK_ERROR' || looksLikeNetworkError(message)) {
    return t('photos.uploadErrorNetwork');
  }

  if (code === 'PHOTO_UPLOAD_STORAGE_ERROR' || code === 'PHOTO_UPLOAD_DATABASE_ERROR') {
    return t('photos.uploadErrorStorage');
  }

  if (message.toLowerCase().includes('5mb')) {
    return t('photos.fileTooLarge');
  }

  return t(fallbackKey);
};
